import { Component, Input, OnInit } from '@angular/core';
import { RoleModel } from '../role.model';
import { ILightWeightObject } from '@fi-shared/models';
import { CheckboxListModel } from '@fi-shared/checkbox-list';
import { comparator } from '@fi-shared/utils/array-sort-comparators';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { AppTextService } from '@fi-shared/app-text';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { ModuleType } from '@fi-shared/types/module-type';
import { ObjectTypes } from '@fi-shared/types/object-type';
import {
    DialogYesNoInputComponent,
    IDialogYesNoInputData,
} from '@fi-app/utilities/dialog/dialog-yes-no-input/dialog-yes-no-input.component';
import { RoleService } from '../services/role.service';
import { FiSnackBarService } from '@fi-shared/fi-shared-services';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';

@Component({
    selector: 'fi-role-share-content',
    templateUrl: './role-share-content.component.html',
    styleUrls: ['./role-share-content.component.scss'],
    standalone: false,
})
export class RoleShareContentComponent implements OnInit {
    @Input() role: RoleModel;
    @Input() allRoles: ILightWeightObject[];

    PostsRolesListData = new CheckboxListModel();
    ForumsRolesListData = new CheckboxListModel();
    PagesFilesRolesListData = new CheckboxListModel();

    signText: string;

    constructor(
        private statisticService: StatisticService,
        private appTextService: AppTextService,
        private loadingService: LoadingService,
        private fiDialogsService: FiDialogsService,
        private roleService: RoleService,
        private snackBar: FiSnackBarService,
        private stateService: FiStateService,
        private customerDataService: FiCustomerDataService,
        private userDataService: FiUserDataService,
    ) {}

    ngOnInit() {
        this.fillRolesList(this.PostsRolesListData);
        this.fillRolesList(this.ForumsRolesListData);
        this.fillRolesList(this.PagesFilesRolesListData);

        this.statisticService.Visit(ModuleType.Core, ObjectTypes.Role, this.role.Id);
    }

    confirmClick() {
        const compareValue = this.appTextService.get('common.verify').toUpperCase();

        const dialogConfirmData: IDialogYesNoInputData = {
            message: this.appTextService.get('area_delete.confirm_dialog_text', {
                '{confirm_word}': compareValue,
            }),
            buttonText: this.appTextService.get('common.confirm'),
            confirmTextToInput: compareValue,
        };

        this.fiDialogsService
            .custom({
                headerText: this.appTextService.get('common.warning'),
                contentComponentRef: DialogYesNoInputComponent,
                contentComponentData: dialogConfirmData,
                config: {
                    maxWidth: '400px',
                },
            })
            .onYesButtonClick(() => {
                this.loadingService
                    .trackPromise(this.roleService.shareContent(this.getShareContentModel()))
                    .then(() => {
                        this.snackBar.showSnackbar({
                            textMessage: this.appTextService.get('role.sharing_selections_updated'),
                            buttonText: this.appTextService.get('common.dismiss'),
                        });
                        this.stateService.go(FiStateNames.Role.List);
                    });
            });
    }

    private getShareContentModel() {
        return {
            RoleId: this.role.Id,
            PostsRoleIds: this.getCheckedItemsFromDataSet(this.PostsRolesListData.DataSet).map((item) => item.Id),
            ForumsRolesIds: this.getCheckedItemsFromDataSet(this.ForumsRolesListData.DataSet).map((item) => item.Id),
            PagesFilesRolesIds: this.getCheckedItemsFromDataSet(this.PagesFilesRolesListData.DataSet).map(
                (item) => item.Id,
            ),
            SignText: this.signText,
        };
    }

    getChangesTexts() {
        const texts = [];
        const postsRoles = this.getCheckedItemsFromDataSet(this.PostsRolesListData.DataSet);
        const forumsRoles = this.getCheckedItemsFromDataSet(this.ForumsRolesListData.DataSet);
        const pagesFilesRoles = this.getCheckedItemsFromDataSet(this.PagesFilesRolesListData.DataSet);
        if (postsRoles.length) {
            const text = this.appTextService
                .get('role.share_confirmation_info')
                .replace('{0}', this.appTextService.get('role.share_posts_title'))
                .replace('{1}', postsRoles.map((item) => item.Name).join(', '))
                .replace('{2}', this.role.Name);
            texts.push(text);
        }
        if (forumsRoles.length) {
            const text = this.appTextService
                .get('role.share_confirmation_info')
                .replace('{0}', this.appTextService.get('role.share_forums_title'))
                .replace('{1}', forumsRoles.map((item) => item.Name).join(', '))
                .replace('{2}', this.role.Name);
            texts.push(text);
        }
        if (pagesFilesRoles.length) {
            const text = this.appTextService
                .get('role.share_confirmation_info')
                .replace('{0}', this.appTextService.get('role.share_pages_files_title'))
                .replace('{1}', pagesFilesRoles.map((item) => item.Name).join(', '))
                .replace('{2}', this.role.Name);
            texts.push(text);
        }
        return texts;
    }

    getSaveDescription() {
        return this.appTextService.get('role.share_save_description', {
            '{fullName}': this.userDataService.getUserFullName(),
            '{userIPAddress}': this.customerDataService.customerData.UserIPAddress,
        });
    }

    private getCheckedItemsFromDataSet(dataSet: Array<any>) {
        return dataSet.filter((item) => item.Checked);
    }

    private fillRolesList(list: CheckboxListModel) {
        list.DataNameField = 'Name';
        list.DataIdField = 'Id';
        list.HeaderName = this.appTextService.get('common.all');
        list.ShowHeader = true;
        list.DataSet = this.allRoles
            .sort(comparator((role) => role.Name))
            .filter((role) => role.Id !== this.role.Id)
            .map((role) => ({
                Id: role.Id,
                Name: role.Name,
            }));
    }
}
